import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, UserInput, Tenant } from '../types';

// Schema base
const baseSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('E-mail inválido'),
  role: z.enum(['SUPERADMIN', 'ADMIN', 'USER']),
  ativo: z.boolean(),
  tenantId: z.string().optional(),
});

// Schema create with required password
const createUserSchema = baseSchema.extend({
  senha: z.string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Senha deve conter pelo menos uma letra minúscula, uma maiúscula e um número'),
}).superRefine((data) => {
  if (data.role !== 'SUPERADMIN' && !data.tenantId && typeof data.tenantId !== 'undefined') {
    // Check if tenantId should be required - this logic is partially duplicated from the component
    // But here we might not know if tenants are available.
    // Ideally, we validate this refinement based on presence of availableTenants prop, but Zod schema is static here.
    // Instead we can rely on backend validation or make the schema dynamic.
    // Trying a simpler approach: validation inside component submit header if needed OR dynamic schema construction.
  }
});

// Schema update with optional password
const updateUserSchema = baseSchema.extend({
  senha: z.string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Senha deve conter pelo menos uma letra minúscula, uma maiúscula e um número')
    .optional(),
});

type UserFormData = z.infer<typeof createUserSchema>;

interface UserFormProps {
  user?: User;
  onSubmit: (data: UserInput & { tenantId?: string }) => Promise<void>;
  onCancel: () => void;
  allowedRoles?: string[];
  availableTenants?: Tenant[];
}

export function UserForm({ user, onSubmit, onCancel, allowedRoles = ['USER'], availableTenants = [] }: UserFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dynamic schema based on needs, but keeping it simple with manual validation where Zod is complex
  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<UserFormData>({
    resolver: zodResolver(user ? updateUserSchema : createUserSchema),
    defaultValues: user ? {
      nome: user.nome,
      email: user.email,
      role: user.role as 'SUPERADMIN' | 'ADMIN' | 'USER',
      ativo: user.ativo,
      tenantId: (user as User & { tenantId?: string }).tenantId || '', // Handle implementation detail if passed
    } : {
      nome: '',
      email: '',
      senha: '',
      role: 'USER',
      ativo: true,
      tenantId: '',
    },
  });

  const selectedRole = watch('role');
  const showTenantSelector = availableTenants.length > 0 && selectedRole !== 'SUPERADMIN';

  const handleFormSubmit = async (data: UserFormData) => {
    // Custom validation for Tenant ID
    if (showTenantSelector && !data.tenantId) {
      setError('tenantId', { message: 'Selecione um tenant para este usuário' });
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData: UserInput & { tenantId?: string } = {
        nome: data.nome,
        email: data.email,
        role: data.role,
        ativo: data.ativo,
      };

      if (data.senha) {
        submitData.senha = data.senha;
      }

      if (data.tenantId && data.role !== 'SUPERADMIN') {
        submitData.tenantId = data.tenantId;
      }

      await onSubmit(submitData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'SUPERADMIN': return 'Super Administrador';
      case 'ADMIN': return 'Administrador';
      case 'USER': return 'Usuário';
      default: return role;
    }
  };

  return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onCancel} />

            <div className="bg-[#0f172a]/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-md relative z-10 overflow-hidden animate-scaleIn border border-white/10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/10 rounded-full blur-3xl -mr-16 -mt-16" />
                
                {/* Header com gradiente */}
                <div className="relative px-8 py-7 bg-white/5 border-b border-white/10 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-transparent" />
                    <h3 className="relative text-2xl font-bold text-white font-oswald tracking-tight uppercase">
                        {user ? 'EDITAR USUÁRIO' : 'NOVO USUÁRIO'}
                    </h3>
                    <p className="relative text-gray-400 text-sm mt-1 font-medium">
                        {user ? 'Atualize as informações de acesso do membro' : 'Cadastre um novo membro no seu workspace'}
                    </p>
                </div>

                <div className="p-8">
                    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
                        <div className="group">
                            <label htmlFor="nome" className="block text-[10px] font-bold text-gray-400 mb-2 font-oswald tracking-[0.2em] uppercase ml-1 group-focus-within:text-blue-400 transition-colors">
                                Nome Completo
                            </label>
                            <input
                                {...register('nome')}
                                type="text"
                                id="nome"
                                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all outline-none text-white placeholder-gray-600 font-medium"
                                placeholder="Ex: João Silva"
                                disabled={isSubmitting}
                            />
                            {errors.nome && (
                                <p className="mt-2 text-[10px] text-red-400 font-bold uppercase tracking-widest flex items-center gap-1 ml-1">
                                    <span>⚠️</span> {errors.nome.message}
                                </p>
                            )}
                        </div>

                        <div className="group">
                            <label htmlFor="email" className="block text-[10px] font-bold text-gray-400 mb-2 font-oswald tracking-[0.2em] uppercase ml-1 group-focus-within:text-blue-400 transition-colors">
                                Endereço de E-mail
                            </label>
                            <input
                                {...register('email')}
                                type="email"
                                id="email"
                                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all outline-none text-white placeholder-gray-600 font-medium"
                                placeholder="email@empresa.com"
                                disabled={isSubmitting}
                            />
                            {errors.email && (
                                <p className="mt-2 text-[10px] text-red-400 font-bold uppercase tracking-widest flex items-center gap-1 ml-1">
                                    <span>⚠️</span> {errors.email.message}
                                </p>
                            )}
                        </div>

                        <div className="group">
                            <label htmlFor="senha" className="block text-[10px] font-bold text-gray-400 mb-2 font-oswald tracking-[0.2em] uppercase ml-1 group-focus-within:text-blue-400 transition-colors">
                                {user ? 'Nova Senha (Opcional)' : 'Senha de Acesso'}
                            </label>
                            <input
                                {...register('senha')}
                                type="password"
                                id="senha"
                                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all outline-none text-white placeholder-gray-600 font-medium"
                                disabled={isSubmitting}
                                placeholder={user ? '••••••••' : 'Mínimo 6 caracteres...'}
                            />
                            {!user && (
                                <p className="mt-2 text-[10px] text-gray-500 font-bold uppercase tracking-widest ml-1">
                                    Combine letras (A-z) e números (0-9)
                                </p>
                            )}
                            {errors.senha && (
                                <p className="mt-2 text-[10px] text-red-400 font-bold uppercase tracking-widest flex items-center gap-1 ml-1">
                                    <span>⚠️</span> {errors.senha.message}
                                </p>
                            )}
                        </div>

                        <div className="group">
                            <label htmlFor="role" className="block text-[10px] font-bold text-gray-400 mb-2 font-oswald tracking-[0.2em] uppercase ml-1 group-focus-within:text-blue-400 transition-colors">
                                Nível de Acesso
                            </label>
                            <div className="relative">
                                <select
                                    {...register('role')}
                                    id="role"
                                    className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all outline-none text-white font-bold appearance-none cursor-pointer"
                                    disabled={isSubmitting}
                                >
                                    {allowedRoles.map((role) => (
                                        <option key={role} value={role} className="bg-[#0f172a] text-white">
                                            {getRoleLabel(role)}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center px-5 pointer-events-none text-gray-500">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {errors.role && (
                                <p className="mt-2 text-[10px] text-red-400 font-bold uppercase tracking-widest flex items-center gap-1 ml-1">
                                    <span>⚠️</span> {errors.role.message}
                                </p>
                            )}
                        </div>

                        {showTenantSelector && (
                            <div className="animate-fadeIn group">
                                <label htmlFor="tenantId" className="block text-[10px] font-bold text-gray-400 mb-2 font-oswald tracking-[0.2em] uppercase ml-1 group-focus-within:text-blue-400 transition-colors">
                                    Organização (Tenant)
                                </label>
                                <div className="relative">
                                    <select
                                        {...register('tenantId')}
                                        id="tenantId"
                                        className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all outline-none text-white font-bold appearance-none cursor-pointer"
                                        disabled={isSubmitting}
                                    >
                                        <option value="" className="bg-[#0f172a] text-white">Selecione uma organização...</option>
                                        {availableTenants.map((tenant) => (
                                            <option key={tenant.id} value={tenant.id} className="bg-[#0f172a] text-white">
                                                {tenant.name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center px-5 pointer-events-none text-gray-500">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                                {errors.tenantId && (
                                    <p className="mt-2 text-[10px] text-red-400 font-bold uppercase tracking-widest flex items-center gap-1 ml-1">
                                        <span>⚠️</span> {errors.tenantId.message}
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="flex items-center p-5 bg-white/5 rounded-2xl border border-white/10 group/check hover:bg-white/10 transition-colors cursor-pointer">
                            <input
                                {...register('ativo')}
                                type="checkbox"
                                id="ativo"
                                className="w-6 h-6 text-blue-600 focus:ring-blue-600/50 border-white/20 rounded-lg cursor-pointer bg-white/5 transition-all"
                                disabled={isSubmitting}
                            />
                            <label htmlFor="ativo" className="ml-4 block text-[10px] font-bold text-gray-400 group-hover/check:text-white uppercase tracking-widest cursor-pointer select-none transition-colors">
                                Usuário Ativo no Sistema
                            </label>
                        </div>

                        <div className="flex gap-4 pt-6 border-t border-white/10">
                            <button
                                type="button"
                                onClick={onCancel}
                                className="flex-1 px-6 py-4 text-[10px] font-bold text-gray-400 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 hover:text-white uppercase tracking-[0.2em] font-oswald transition-all active:scale-95"
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="flex-1 px-6 py-4 text-[10px] font-bold text-white bg-blue-600 rounded-2xl hover:bg-blue-500 uppercase tracking-[0.2em] font-oswald transition-all shadow-xl shadow-blue-900/40 hover:shadow-blue-900/60 hover:-translate-y-1 active:scale-95 border border-blue-400/20 disabled:opacity-50 disabled:transform-none"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                        <span>Salvando...</span>
                                    </div>
                                ) : (user ? 'Salvar Alterações' : 'Criar Usuário')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}